import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Browser } from '@capacitor/browser';
import { select, Store } from '@ngrx/store';

import { each, has, omit } from 'lodash-es';
import { filter } from 'rxjs';

import { GenericComponent } from '../../misc/components/generic/generic.component';
import * as fromDataProviderActions from '../../store/actions/data-provider.actions';
import * as fromDataPortalSelectors from '../../store/selectors/data-portal.selectors';
import * as fromDataProviderSelectors from '../../store/selectors/data-provider.selectors';
import { FormValidationErrors } from '../../typings/form-validation-errors';

interface ProviderLoginFormGroup {
  password: FormControl<string | null>;
  username: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-login',
  standalone: false,
  templateUrl: './login.page.html'
})
export class LoginPageComponent extends GenericComponent {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  dataPortalUrl = '#';
  loginForm: FormGroup<ProviderLoginFormGroup> = new FormGroup({
    password: new FormControl<string>('', Validators.required),
    username: new FormControl<string>('', Validators.required)
  });
  loginFormValidationErrors: FormValidationErrors = {};

  constructor() {
    super();
    this.loginForm.controls.username.valueChanges
      .pipe(filter(() => !!this.loginForm.controls.password.errors))
      .subscribe(() => {
        this.loginForm.controls.password.setErrors(omit(this.loginForm.controls.password.errors, ['invalid']));
        this.loginForm.controls.password.updateValueAndValidity();
      });
  }

  ionViewDidEnter() {
    this.loginForm.reset();
    this.subscriptions.push(
      this.store.pipe(select(fromDataPortalSelectors.selectUrl)).subscribe((url) => {
        this.dataPortalUrl = url ? url : '#';
        this.changeDetectorRef.detectChanges();
      })
    );
    this.addUniqueSubscription(
      'formValidationErrors',
      this.store
        .pipe(select(fromDataProviderSelectors.selectLoginFormValidationErrors))
        .subscribe((formValidationErrors) => {
          this.loginFormValidationErrors = formValidationErrors;
          const controlNames = ['username', 'password'];
          each(controlNames, (controlName) => {
            const control = this.loginForm.get(controlName);
            if (has(this.loginFormValidationErrors, controlName)) {
              control?.setErrors(this.loginFormValidationErrors[controlName]);
            } else {
              control?.setErrors(null);
            }
          });
          this.changeDetectorRef.detectChanges();
        })
    );
  }

  onLogin() {
    const password = this.loginForm.get('password')?.value ?? null;
    this.store.dispatch(
      fromDataProviderActions.authenticateDataProvider({
        payload: {
          data: {
            password: password ? btoa(password) : '',
            username: this.loginForm.get('username')?.value ?? ''
          },
          isOnboardingFlow: true
        }
      })
    );
  }

  async onHelp() {
    await Browser.open({
      url: this.dataPortalUrl,
      windowName: '_blank'
    });
  }
}
